import { useCallback, useMemo, useRef } from 'react';
import { useExternalStoreRuntime, type ThreadMessageLike } from '@assistant-ui/react';
import { useEmbeddedIvyChatStore } from '@/stores/embeddedIvyChatStore';
import { useOutlineEditQueueStore, composeQueuedMessage } from '@/stores/outlineEditQueueStore';
import type { UIMessage } from './messageParser';
import { isOutOfCreditsMessage, outOfCreditsPart } from '@/services/billingPaywallDetector';
import { errorPart, turnMetaPart } from './chatNotices';
import { stepErrorText } from './stepWords';
import { splitTags } from '@/lib/studioB/takeover';

// Stable identity convertMessage so the options object passed to
// useExternalStoreRuntime doesn't churn each render.
const convertMessage = (msg: ThreadMessageLike) => msg;

function deduplicateMessages(messages: UIMessage[]): UIMessage[] {
  const seen = new Set<string>();
  const seenToolUseIds = new Set<string>();
  return messages.filter(msg => {
    if (seen.has(msg.id)) return false;
    seen.add(msg.id);
    // Deduplicate tool_use by toolUseId (same tool call can appear from history + reconnection)
    if (msg.type === 'tool_use' && msg.toolUseId) {
      if (seenToolUseIds.has(msg.toolUseId)) return false;
      seenToolUseIds.add(msg.toolUseId);
    }
    // Deduplicate tool_result by toolUseId
    if (msg.type === 'tool_result' && msg.toolUseId) {
      if (seenToolUseIds.has(`result-${msg.toolUseId}`)) return false;
      seenToolUseIds.add(`result-${msg.toolUseId}`);
    }
    return true;
  });
}

export function mergeIntoTurns(messages: UIMessage[]): ThreadMessageLike[] {
  const deduped = deduplicateMessages(messages);
  const result: ThreadMessageLike[] = [];
  let currentAssistantParts: any[] = [];
  let currentAssistantId: string | null = null;
  let currentTimestamp: Date | null = null;

  const resultMap = new Map<string, UIMessage>();
  for (const msg of deduped) {
    if (msg.type === 'tool_result' && msg.toolUseId) {
      resultMap.set(msg.toolUseId, msg);
    }
  }

  function flushAssistant() {
    if (currentAssistantParts.length > 0 && currentAssistantId) {
      result.push({
        id: currentAssistantId,
        role: 'assistant',
        content: currentAssistantParts,
        createdAt: currentTimestamp || undefined,
      });
      currentAssistantParts = [];
      currentAssistantId = null;
      currentTimestamp = null;
    }
  }

  for (const msg of deduped) {
    switch (msg.type) {
      case 'chat': {
        if (msg.role === 'user') {
          flushAssistant();
          result.push({
            id: msg.id,
            role: 'user',
            content: [{ type: 'text', text: msg.content }],
            createdAt: new Date(msg.timestamp),
          });
        } else {
          if (!currentAssistantId) {
            currentAssistantId = msg.id;
            currentTimestamp = new Date(msg.timestamp);
          }
          if (msg.content.trim()) {
            currentAssistantParts.push({ type: 'text', text: msg.content });
          }
        }
        break;
      }

      case 'thinking': {
        if (!currentAssistantId) {
          currentAssistantId = msg.id;
          currentTimestamp = new Date(msg.timestamp);
        }
        currentAssistantParts.push({ type: 'reasoning', text: msg.content });
        break;
      }

      case 'tool_use': {
        if (!currentAssistantId) {
          currentAssistantId = msg.id;
          currentTimestamp = new Date(msg.timestamp);
        }
        const toolResult = msg.toolUseId ? resultMap.get(msg.toolUseId) : undefined;
        currentAssistantParts.push({
          type: 'tool-call',
          toolCallId: msg.toolUseId || msg.id,
          toolName: msg.toolName || 'unknown',
          args: JSON.parse(JSON.stringify(msg.toolInput || {})),
          result: toolResult?.isError ? stepErrorText(toolResult.content) : toolResult?.content,
          isError: toolResult?.isError || false,
          startedAt: msg.timestamp,
          ...(toolResult && { endedAt: toolResult.timestamp }),
          ...(msg.parentToolUseId && { parentToolUseId: msg.parentToolUseId }),
        });
        break;
      }

      case 'tool_result':
        break;

      case 'result': {
        if (msg.costUsd || msg.durationMs) {
          if (!currentAssistantId) {
            currentAssistantId = msg.id;
            currentTimestamp = new Date(msg.timestamp);
          }
          if (msg.durationMs) {
            currentAssistantParts.push({ type: 'text', text: turnMetaPart(`${(msg.durationMs / 1000).toFixed(1)}s`) });
          }
        }
        break;
      }

      case 'system': {
        if (isOutOfCreditsMessage(msg)) {
          if (!currentAssistantId) {
            currentAssistantId = msg.id;
            currentTimestamp = new Date(msg.timestamp);
          }
          currentAssistantParts.push({ type: 'text', text: outOfCreditsPart(msg) });
        } else if (msg.subtype === 'error') {
          if (!currentAssistantId) {
            currentAssistantId = msg.id;
            currentTimestamp = new Date(msg.timestamp);
          }
          currentAssistantParts.push({ type: 'text', text: errorPart(msg.content) });
        } else if (msg.subtype === 'compaction_summary' || msg.subtype === 'compact_boundary') {
          flushAssistant();
          // Render compaction as a special assistant message with a marker prefix
          // that the thread component can detect and render as a collapsed card
          result.push({
            id: msg.id,
            role: 'assistant',
            content: [{ type: 'text', text: `[COMPACTION_SUMMARY]\n${msg.content}` }],
            createdAt: new Date(msg.timestamp),
          });
        }
        break;
      }
    }
  }

  flushAssistant();
  return result;
}

export function useEmbeddedIvyRuntime(systemContext?: string, conversationId?: string) {
  const messages = useEmbeddedIvyChatStore(s => s.messages);
  const isStreaming = useEmbeddedIvyChatStore(s => s.isStreaming);
  const sendMessage = useEmbeddedIvyChatStore(s => s.sendMessage);
  const abortStream = useEmbeddedIvyChatStore(s => s.abortStream);

  const convertedMessages = useMemo(() => mergeIntoTurns(messages), [messages]);

  // CRITICAL: keep `systemContext` out of the runtime callback's closure.
  //
  // In the video workspace, ivyChatSystemContext recomputes on every slide change
  // (it embeds the active slide id/title). If onNew captured systemContext directly,
  // each slide transition created a new onNew closure, which churned the options
  // object passed to useExternalStoreRuntime, which caused @assistant-ui/react to
  // instantiate a fresh runtime + thread + composer + message + suggestion +
  // attachment + voice + modelContext sub-runtimes — all retained by upstream
  // subscribers. Heap snapshot diff at slide-04 showed +470 MB of strings and
  // ~870 instances of each runtime closure type from this single path.
  //
  // Stashing systemContext in a ref and reading the latest value at call time
  // means the closure is reference-stable across renders, the options object
  // stays referentially stable across slide changes, and the runtime is created
  // exactly once for the chat session.
  const systemContextRef = useRef(systemContext);
  systemContextRef.current = systemContext;
  // conversationId is stable per chat session, so capturing via ref keeps onNew reference-stable.
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;

  const runtimeRef = useRef<ReturnType<typeof useExternalStoreRuntime> | null>(null);

  const onNew = useCallback(async (message: ThreadMessageLike) => {
    const textParts = (message.content as ReadonlyArray<{ type: string; text?: string }>).filter(p => p.type === 'text');
    const text = textParts.map(p => ('text' in p ? p.text ?? '' : '')).join('\n');

    // Queued outline edits (storyboard archetype switch / reorder / description edits) ride this send.
    const convId = conversationIdRef.current;
    const queuedEdits = convId ? useOutlineEditQueueStore.getState().items(convId) : [];

    if (text.trim() || useEmbeddedIvyChatStore.getState().pendingVoice || queuedEdits.length > 0) {
      const { pendingAttachments, setPendingAttachments, pendingVoice, setPendingVoice, pendingTags, setPendingTags } = useEmbeddedIvyChatStore.getState();

      // Build message: prepend voice selection if present
      let finalText = text.trim();
      if (pendingVoice) {
        finalText = finalText
          ? `Use voice: ${pendingVoice}\n\n${finalText}`
          : `Use voice: ${pendingVoice}`;
        setPendingVoice(null);
      }

      if (pendingTags) {
        finalText = finalText ? `${finalText}\n\n${pendingTags}` : pendingTags;
        setPendingTags(null);
      }

      // Storyboard-input lines lead the message; outline edits follow as one combined instruction.
      if (queuedEdits.length > 0 && convId) {
        finalText = composeQueuedMessage(finalText, queuedEdits);
        useOutlineEditQueueStore.getState().clear(convId);
      }

      // Consume pending attachments context. Read systemContext from the ref so
      // we always use the latest value without recreating this callback.
      const sysCtx = systemContextRef.current;
      const fullContext = pendingAttachments
        ? `${sysCtx || ''} ${pendingAttachments}`.trim()
        : sysCtx;
      if (pendingAttachments) setPendingAttachments(null);

      await sendMessage(finalText, fullContext);
    }
  }, [sendMessage]);

  const onCancel = useCallback(async () => {
    const stopped = abortStream();
    // assistant-ui hands a message with no reply yet back to the box as soon as this call returns, tags and all.
    await Promise.resolve();
    const composer = runtimeRef.current?.thread.composer;
    const { words, tags } = splitTags(composer?.getState().text ?? '');
    if (tags) {
      composer?.setText(words);
      useEmbeddedIvyChatStore.getState().setPendingTags(tags);
    }
    await stopped;
  }, [abortStream]);

  const runtime = useExternalStoreRuntime({
    messages: convertedMessages,
    convertMessage,
    isRunning: isStreaming,
    onNew,
    onCancel,
  });
  runtimeRef.current = runtime;

  return runtime;
}
